// ===========================================
// Enquiry UI helpers (enquiries plan §5.7): ONE copy of each, shared by the page, the drawer and
// the dialogs
// ===========================================
// - Stage colours (brand colours only, flat).
// - A stage's label is the SCHOOL's (`GET /config`); the built-in label is only the fallback.
// - Days: every `@db.Date` is shown `dd/mm/yyyy` with UTC getters (`formatUTCDate`), and "is it
//   due / overdue" compares DAY KEYS against `config.schoolToday` (the server's school day),
//   never the browser's day.
// - Stored enquiry text is HTML-escaped (#298): decode it for display, never render it as HTML.

import type {
  EnquiryBallWith,
  EnquiryClientConfig,
  EnquiryOwner,
  EnquiryStage,
  EnquiryStageView,
  EnquiryTemperature,
} from '../../../api/enquiry.api';
import { brandColors, statusColors } from '../../../styles/brandColors';
import { formatUTCDate, toUTCDateKey } from '../../../utils/dateUtils';
import { unescapeHtml } from '../../../utils/sanitize';

export const DEFAULT_STAGE_LABELS: Record<EnquiryStage, string> = {
  NEW: 'New',
  IN_CONVERSATION: 'In Conversation',
  MNG_BOOKED: 'MNG Booked',
  MNG_FOLLOW_UP: 'MNG Follow Up',
  READY_TO_ONBOARD: 'Ready to Onboard',
  ONBOARDING: 'Onboarding',
  WAITLIST: 'Waitlist',
  ENROLLED: 'Enrolled',
  NOT_PROCEEDING: 'Not Proceeding',
  SPAM: 'Spam',
  RESOLVED_TO_REVIEW: 'To review',
};

// Dark text on every chip: white on primary (3.85:1) and on error (3.47:1) is below WCAG AA's 4.5:1
// for chip-size text (review 5.7 L10; the pairs came from the old status chips).
export const STAGE_COLORS: Record<EnquiryStage, { bg: string; text: string }> = {
  NEW: { bg: brandColors.primary, text: brandColors.textPrimary },
  IN_CONVERSATION: { bg: statusColors.infoBg, text: brandColors.textPrimary },
  MNG_BOOKED: { bg: brandColors.coral, text: brandColors.textPrimary },
  MNG_FOLLOW_UP: { bg: statusColors.warningBg, text: brandColors.textPrimary },
  READY_TO_ONBOARD: { bg: brandColors.secondary, text: brandColors.textPrimary },
  ONBOARDING: { bg: statusColors.pendingBg, text: brandColors.textPrimary },
  WAITLIST: { bg: brandColors.cream, text: brandColors.textPrimary },
  ENROLLED: { bg: brandColors.mint, text: brandColors.textPrimary },
  NOT_PROCEEDING: { bg: statusColors.mutedBg, text: brandColors.textPrimary },
  SPAM: { bg: brandColors.error, text: brandColors.textPrimary },
  RESOLVED_TO_REVIEW: { bg: statusColors.mutedBg, text: brandColors.textPrimary },
};

/** The school's label for a stage (Settings → Enquiries), else the built-in one. */
export function stageLabel(config: EnquiryClientConfig | undefined, stage: EnquiryStage): string {
  return config?.stages.find((s) => s.stage === stage)?.label ?? DEFAULT_STAGE_LABELS[stage];
}

export function stageView(config: EnquiryClientConfig | undefined, stage: EnquiryStage): EnquiryStageView | undefined {
  return config?.stages.find((s) => s.stage === stage);
}

/** A `@db.Date` (or `YYYY-MM-DD` key) as `dd/mm/yyyy`; '' for none. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '';
  return formatUTCDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** A `@db.Date` value as its `YYYY-MM-DD` key, or null. */
export function dayKey(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return toUTCDateKey(value);
  } catch {
    return null;
  }
}

export type DueState = 'OVERDUE' | 'TODAY' | 'UPCOMING';

/** Whether a next action is overdue, due today or later, against the SCHOOL's today. */
export function dueState(due: string | null | undefined, schoolToday: string | undefined): DueState | null {
  const key = dayKey(due);
  if (!key || !schoolToday) return null;
  if (key < schoolToday) return 'OVERDUE';
  if (key === schoolToday) return 'TODAY';
  return 'UPCOMING';
}

/** Archived = `archivedAt` set and reached (a Not Proceeding row's is in the future until its window ends). */
export function isArchived(archivedAt: string | null | undefined, now: Date = new Date()): boolean {
  return !!archivedAt && new Date(archivedAt).getTime() <= now.getTime();
}

/**
 * The enquiry MESSAGE is stored HTML-escaped (#298, `escapeHtml` on both create paths): decode it
 * for display, and before copying it anywhere. Nothing else is: names, subject, children, the next
 * action and the closing detail are stored as typed (`sanitizeText`), so decoding them would turn a
 * literal "&amp;" into "&" (review 5.7 L1; production census 23/10/2026: 0 of 79 enquiries hold an
 * entity in a name or subject, 2 in the message). Activity notes are raw too (P44).
 */
export function decodeMessage(text: string | null | undefined): string {
  return unescapeHtml(text ?? '');
}

/** What the history calls each activity type. ENROLMENT reads "Onboarding" (Laura, 22/10/2026). */
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  PHONE_CALL: 'Phone call',
  EMAIL: 'Email',
  SMS: 'SMS',
  MEET_AND_GREET: 'Meet & Greet',
  INTERNAL_NOTE: 'Internal note',
  FOLLOW_UP: 'Follow up',
  ENROLMENT: 'Onboarding',
  STAGE_CHANGE: 'Stage change',
  SYSTEM: 'System',
};

/** The stages a staff member may move an enquiry TO from `from` (the server still judges every rule). */
export function manualTargets(config: EnquiryClientConfig | undefined, from: EnquiryStage): EnquiryStageView[] {
  if (!config) return [];
  return config.stages.filter((s) => s.enabled && s.stage !== from && s.stage !== 'RESOLVED_TO_REVIEW');
}

/**
 * A linked booking in one of these can be REPLACED by creating or linking another (§8.2, the server's
 * `REPLACEABLE_MNG_STATUSES`; review 5.7 H1): an unconfirmed, cancelled or rejected one.
 */
// The ONE frontend copy of the backend's `REPLACEABLE_MNG_STATUSES`
// (apps/backend/src/services/enquiry/enquiryLink.service.ts): keep the two in step.
export const REPLACEABLE_MNG_STATUSES: readonly string[] = ['PENDING_VERIFICATION', 'CANCELLED', 'REJECTED'];

/**
 * A booking the server will NOT replace with a new link (BE `ENQUIRY_ALREADY_LINKED`): everything not
 * in `REPLACEABLE_MNG_STATUSES`. While one is linked, a new booking link is hidden.
 */
export const holdsLiveBooking = (status: string | null | undefined): boolean =>
  !!status && !REPLACEABLE_MNG_STATUSES.includes(status);

/**
 * An id fit for an API path and a drawer: CUID2-shaped (lower-case letters and digits, starting with a
 * letter). A deep link's `?enquiry=` is checked with this before it reaches a request.
 */
export const isEnquiryId = (value: string | null | undefined): value is string =>
  typeof value === 'string' && /^[a-z][a-z0-9]{20,31}$/.test(value);

/** A linked booking that is no booking at all (cancelled or rejected). */
export const isDeadBooking = (status: string | null | undefined): boolean => status === 'CANCELLED' || status === 'REJECTED';

/**
 * The stages a new booking moves the enquiry FROM to MNG Booked (A1's source list, BE
 * `AUTO_RULES.MNG_BOOKED`). From any other stage the booking is recorded and the stage stays
 * (review 5.7 L12).
 */
export const BOOKING_MOVES_FROM: readonly EnquiryStage[] = ['NEW', 'IN_CONVERSATION', 'WAITLIST', 'NOT_PROCEEDING', 'MNG_FOLLOW_UP'];

/** The linked Meet & Greet's teacher as an owner choice: what an MNG_TEACHER stage defaults to (review 5.7 M7). */
export function mngTeacherOption(
  enquiry: { MeetAndGreet: { Teacher?: { User: { id: string; firstName: string; lastName: string } } | null } | null },
): { id: string; name: string } | null {
  const user = enquiry.MeetAndGreet?.Teacher?.User;
  return user ? { id: user.id, name: `${user.firstName} ${user.lastName}`.trim() } : null;
}

// ---- Phase 2: the panel and the list ----

const EXIT_OR_REVIEW: readonly EnquiryStage[] = ['ENROLLED', 'NOT_PROCEEDING', 'SPAM', 'RESOLVED_TO_REVIEW'];

/** A named owner's title or role label (the brief): the owner title, "Support", the role's name, "Teacher". */
export function ownerRoleLabel(owner: Pick<EnquiryOwner, 'labelKind' | 'roleName'>, ownerTitle: string): string | null {
  switch (owner.labelKind) {
    case 'OWNER':
      return ownerTitle;
    case 'SUPPORT':
      return 'Support';
    case 'STAFF':
      return owner.roleName;
    case 'TEACHER':
      return 'Teacher';
    default:
      return null;
  }
}

/**
 * Who owns an enquiry, for the panel and the list: a person with their title or role label
 * ("Laura Smith · Principal", Andrew 24/10/2026, Phase 2 review L11); else the group it waits
 * with, "Unassigned · <role>" or "Unassigned · <owner title>" (§1c); "—" where a stage has no owner.
 */
/** The school's colours for the two login roles an owner chip can name (`useRoleColorScheme`, #629). */
export interface OwnerChipColors {
  owner: string;
  teacher: string;
}

export function ownerLabel(
  enquiry: {
    stage: EnquiryStage;
    ownerTarget: string | null;
    AssignedTo: (Pick<EnquiryOwner, 'firstName' | 'lastName'> & Partial<Pick<EnquiryOwner, 'labelKind' | 'roleName' | 'roleColor'>>) | null;
    OwnerRole?: { name: string; color?: string | null } | null;
  },
  ownerTitle: string,
  colors?: OwnerChipColors,
): { text: string; name: string; role: string | null; roleColor: string | null; unassigned: boolean } {
  // `text` is the one-line form (the panel); `name` + `role` the two-line one (the list's chip),
  // `roleColor` the chip's fill: the owner tier and a teacher by the school's User Role Colors, a
  // staff member or a waiting group by their custom role's colour, a support account none (#629).
  const shape = (name: string, role: string | null, unassigned: boolean, roleColor: string | null = null) =>
    ({ text: role ? `${name} · ${role}` : name, name, role, roleColor: role ? roleColor : null, unassigned });
  if (enquiry.AssignedTo) {
    const name = `${enquiry.AssignedTo.firstName} ${enquiry.AssignedTo.lastName}`.trim();
    const kind = enquiry.AssignedTo.labelKind ?? null;
    const label = ownerRoleLabel({ labelKind: kind, roleName: enquiry.AssignedTo.roleName ?? null }, ownerTitle);
    const color = kind === 'OWNER' ? colors?.owner
      : kind === 'TEACHER' ? colors?.teacher
        : kind === 'STAFF' ? enquiry.AssignedTo.roleColor
          : null;
    return shape(name, label || null, false, color ?? null);
  }
  if (EXIT_OR_REVIEW.includes(enquiry.stage)) return shape('—', null, false);
  if (enquiry.OwnerRole) return shape('Unassigned', enquiry.OwnerRole.name, true, enquiry.OwnerRole.color ?? null);
  if (enquiry.ownerTarget === 'ADMIN_TIER') return shape('Unassigned', ownerTitle, true, colors?.owner ?? null);
  return shape('Unassigned', null, true);
}

export const TEMPERATURE_LABELS: Record<EnquiryTemperature, string> = { COLD: 'Cold', WARM: 'Warm', HOT: 'Hot' };

// Flat brand colours with dark text (the STAGE_COLORS contrast rule).
export const TEMPERATURE_COLORS: Record<EnquiryTemperature, { bg: string; text: string }> = {
  COLD: { bg: statusColors.infoBg, text: brandColors.textPrimary },
  WARM: { bg: brandColors.secondary, text: brandColors.textPrimary },
  HOT: { bg: brandColors.coral, text: brandColors.textPrimary },
};

/** "Ball with": the school's own word for itself (Settings → Enquiries), else "School". */
export function ballWithLabel(config: EnquiryClientConfig | undefined, ballWith: EnquiryBallWith): string {
  if (ballWith === 'FAMILY') return 'Family';
  return config?.settings.ballWithSchoolLabel?.trim() || 'School';
}

/** An exit or to-review enquiry carries no ball (§1a): the column shows nothing. */
export const carriesBall = (stage: EnquiryStage): boolean => !EXIT_OR_REVIEW.includes(stage);

/** What the family is interested in: each child's interest, else the legacy instrument. */
export function interestSummary(enquiry: { Children: Array<{ interest: string | null }>; instrument: string | null }): string {
  const interests = enquiry.Children.map((c) => c.interest?.trim()).filter((i): i is string => !!i);
  if (interests.length > 0) return Array.from(new Set(interests)).join(', ');
  return enquiry.instrument ?? '';
}

export interface InterestChip {
  key: string;
  label: string;
  /** The matched instrument's colour; null = a plain chip (no match, or the instrument has none). */
  color: string | null;
}

/**
 * The Interest column as chips: one per distinct interest, in the children's order. A child whose
 * interest matched one of the school's instruments shows that instrument's name and colour (two
 * children learning Piano give one Piano chip); anything else shows as typed, uncoloured. An
 * enquiry with no child interest falls back to the legacy `instrument` text.
 */
export function interestChips(enquiry: {
  Children: Array<{ interest: string | null; Instrument?: { id: string; name: string; color: string | null } | null }>;
  instrument: string | null;
}): InterestChip[] {
  const chips: InterestChip[] = [];
  const seen = new Set<string>();
  for (const child of enquiry.Children) {
    const typed = child.interest?.trim();
    const chip: InterestChip | null = child.Instrument
      ? { key: `i:${child.Instrument.id}`, label: child.Instrument.name, color: child.Instrument.color || null }
      : typed
        ? { key: `t:${typed.toLowerCase()}`, label: typed, color: null }
        : null;
    if (chip && !seen.has(chip.key)) {
      seen.add(chip.key);
      chips.push(chip);
    }
  }
  const legacy = enquiry.instrument?.trim();
  if (chips.length === 0 && legacy) chips.push({ key: `t:${legacy.toLowerCase()}`, label: legacy, color: null });
  return chips;
}

// ---- Save as template: never one family's link or names (review M2, Andrew 26/10/2026) ----

/** Mirrors the backend's `SIGNED_BOOKING_LINK` (`enquiryReplyMergeFields.ts`): a link signed for one enquiry. */
const SIGNED_BOOKING_LINK = /(?:https?:\/\/)?[^\s()<>"'[\]]*?\/meet-and-greet\?e=[A-Za-z0-9]+(?:\.|%2E)[A-Fa-f0-9]+/gi;

/** A signed booking link back to `{{bookingLink}}` (the server does the same on every save). */
export function replaceSignedBookingLinks(text: string): { text: string; replaced: number } {
  let replaced = 0;
  const out = text.replace(SIGNED_BOOKING_LINK, () => {
    replaced += 1;
    return '{{bookingLink}}';
  });
  return { text: out, replaced };
}

/** Mirrors the backend's `joinChildNames`: "Mia", "Mia and Leo", "Mia, Leo and Sam" (null for none). */
export function joinChildNames(names: Array<string | null | undefined>): string | null {
  const clean = names.map((n) => n?.trim()).filter((n): n is string => !!n);
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

export interface FamilyNames {
  parentFirstName: string | null;
  parentLastName: string | null;
  childFirstNames: Array<string | null>;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A whole word: not inside another word (Unicode letters, so "Siobhán" works and "Ada" is not found in "Adam"). */
const wordPattern = (name: string) => new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, 'gu');

/** This family's names that appear in `text` as whole words, each once, in a stable order. */
export function familyNamesIn(text: string, family: FamilyNames): string[] {
  const names = [family.parentFirstName, family.parentLastName, ...family.childFirstNames]
    .map((n) => n?.trim())
    .filter((n): n is string => !!n && n.length > 1);
  return [...new Set(names)].filter((n) => wordPattern(n).test(text));
}

/**
 * Swap this family's names for merge fields: the children as the render joins them ("Mia and Leo")
 * to `{{childNames}}` first, then the parent's first name to `{{parentName}}`. A name used any other
 * way (one child of two on its own, a surname) is left for the person to change: `familyNamesIn`
 * still finds it, and the dialog says so.
 */
export function swapFamilyNames(text: string, family: FamilyNames): string {
  let out = text;
  const children = joinChildNames(family.childFirstNames);
  if (children) out = out.replace(wordPattern(children), '{{childNames}}');
  const parent = family.parentFirstName?.trim();
  if (parent) out = out.replace(wordPattern(parent), '{{parentName}}');
  return out;
}

/** The first occurrence of each `id`, order kept (offset paging can deliver one entry on two pages). */
export function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
}

/** A phone number as a `tel:` link target (digits and a leading +). */
export const telHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

/** "Mark enrolled" is offered from Onboarding, or from Ready to Onboard when Onboarding is off (P40). */
export function canMarkEnrolled(config: EnquiryClientConfig | undefined, from: EnquiryStage): boolean {
  if (from === 'ONBOARDING') return true;
  return from === 'READY_TO_ONBOARD' && stageView(config, 'ONBOARDING')?.enabled === false;
}
